import {FilePdf24} from "@bphxd/ds-core-react/lib/icons";
import {Link} from "react-router-dom";
import {UncontrolledTooltip} from "reactstrap";

import {status} from "modules/GlobalMassBalance/constants";
import {
  getFilterValue,
  getStatusColor,
  getStatusValue,
  isNumberAvailable,
} from "modules/GlobalMassBalance/utils";

import moment from "moment";
import {parseAndRoundToThreeDecimal} from "utils/numberUtil";
import ActionDropdown from "../ActionDropdown";
import ColumnHighlight from "../ColumnHighlight";

import "./index.scss";

export const getColumnsTR = (
  dateFormat,
  decimalFormatNew,
  selectedCountry,
  navigateToAllocation,
  currentFilterValues,
  siteReferenceId,
  divisionId,
) => [
  {
    header: "Incoming",
    columns: [
      {
        header: "Physical receipt date",
        accessorKey: "physicalReceiptDate",
        key: "physicalReceiptDate",
        visible: true,
        meta: {
          filterVariant: "daterange",
        },
        filterFn: "dateRangeFilterFn",
        accessorFn: (row) => {
          return row?.inboundRecord?.physicalReceiptDate;
        },
        cell: ({cell}) => {
          return cell.getValue() && moment(cell.getValue()).format(dateFormat);
        },
      },
      {
        header: "Document",
        accessorKey: "incomingDocument",
        key: "incomingDocument",
        visible: true,
        size: 500,
        accessorFn: (row) => {
          return row?.inboundRecord?.incomingDocument || "";
        },
        cell: ({cell, row}) => {
          const tooltipId = `incomingDocument${row?.id}`;
          return (
            <>
              <Link
                to={{
                  pathname: `/document-manager/incoming/${selectedCountry}/detailedView/${encodeURIComponent(
                    row?.original?.inboundRecord?.certificateInboundEuId,
                  )}`,
                  search: `?siteReferenceId=${siteReferenceId}&divisionId=${divisionId}`,
                }}
                className="link-dark"
                id={tooltipId}
              >
                <u className="flex">
                  {cell.getValue() && <FilePdf24 />}
                  <ColumnHighlight
                    value={cell.getValue()}
                    columnFilter={getFilterValue(cell, "incomingDocument")}
                    hasEllipsis
                    isCertificate
                  />
                </u>
              </Link>
              <UncontrolledTooltip placement="bottom" target={tooltipId}>
                {cell.getValue() ?? ""}
              </UncontrolledTooltip>
            </>
          );
        },
      },
      {
        header: "Raw material",
        accessorKey: "rawMaterial",
        key: "rawMaterial",
        visible: true,
        meta: {
          filterVariant: "select",
        },
        accessorFn: (row) => {
          return row?.inboundRecord?.rawMaterial;
        },
      },
      {
        header: "Country of origin",
        accessorKey: "countryOfOrigin",
        key: "countryOfOrigin",
        visible: true,
        meta: {
          filterVariant: "select",
        },
        accessorFn: (row) => {
          return row?.inboundRecord?.countryOfOrigin;
        },
      },
      {
        header: "GHG total gCO2eq/MJ",
        accessorKey: "ghgTotalMj",
        key: "ghgTotalMj",
        visible: true,
        dataType: "number",
        accessorFn: (row) => {
          return isNumberAvailable(row?.inboundRecord?.ghgTotalMj)
            ? parseAndRoundToThreeDecimal(
                row?.inboundRecord?.ghgTotalMj,
              ).toString()
            : "";
        },
        cell: ({cell}) => {
          return cell.getValue();
        },
      },
      {
        header: "Actualized quantity (in) m³",
        accessorKey: "actualizedQuantityM3",
        key: "actualizedQuantityM3",
        visible: true,
        disabled: true,
        dataType: "number",
        accessorFn: (row) => {
          return isNumberAvailable(row?.inboundRecord?.actualizedQuantityM3)
            ? parseAndRoundToThreeDecimal(
                row?.inboundRecord?.actualizedQuantityM3,
              ).toString()
            : "";
        },
        cell: ({cell, row}) => {
          const isPending =
            row?.original.outboundRecords.length === 1 &&
            row?.original.outboundRecords?.[0]?.status === status.PENDING;
          return (
            <span
              style={{
                color: isPending ? "rgba(17, 17, 17, 0.31)" : "auto",
              }}
            >
              {cell.getValue()}
            </span>
          );
        },
      },
      {
        header: "Issuance date",
        accessorKey: "issuanceDate",
        key: "issuanceDate",
        visible: false,
        meta: {
          filterVariant: "daterange",
        },
        filterFn: "dateRangeFilterFn",
        accessorFn: (row) => {
          return row?.inboundRecord?.issuanceDate;
        },
        cell: ({cell}) => {
          return (
            cell.getValue() &&
            moment(cell.getValue()).utcOffset(0).format(dateFormat)
          );
        },
      },
      {
        header: "Quantity(in) m³",
        accessorKey: "productQtyM3",
        key: "productQtyM3",
        visible: false,
        dataType: "number",
        accessorFn: (row) => {
          return isNumberAvailable(row?.inboundRecord?.productQtyM3)
            ? parseAndRoundToThreeDecimal(
                row?.inboundRecord?.productQtyM3,
              ).toString()
            : "";
        },
      },
      {
        header: "Quantity MT",
        accessorKey: "productQtyMt",
        key: "productQtyMt",
        visible: false,
        dataType: "number",
        accessorFn: (row) => {
          return isNumberAvailable(row?.inboundRecord?.productQtyMt)
            ? parseAndRoundToThreeDecimal(
                row?.inboundRecord?.productQtyMt,
              ).toString()
            : "";
        },
      },
      {
        header: "Supplier",
        accessorKey: "supplier",
        key: "supplier",
        visible: false,
        meta: {
          filterVariant: "select",
        },
        accessorFn: (row) => {
          return row?.inboundRecord?.supplier;
        },
      },
      {
        header: "Transport system",
        accessorKey: "transportSystem",
        key: "transportSystem",
        visible: false,
        meta: {
          filterVariant: "select",
        },
        accessorFn: (row) => {
          return row?.inboundRecord?.transportSystem;
        },
      },
      {
        header: "Vessel name",
        accessorKey: "vesselName",
        key: "vesselName",
        visible: false,
        meta: {
          filterVariant: "select",
        },
        accessorFn: (row) => {
          return row?.inboundRecord?.vesselName;
        },
      },
      {
        header: "Invoice number",
        accessorKey: "invoiceNumber",
        key: "invoiceNumber",
        visible: false,
        accessorFn: (row) => {
          return row?.inboundRecord?.invoiceNumber || "";
        },
      },
      {
        header: "Transport loss/gain",
        accessorKey: "transportLg",
        key: "transportLg",
        visible: false,
        meta: {
          filterVariant: "select",
        },
        accessorFn: (row) => {
          return row?.inboundRecord?.transportLg;
        },
      },
      {
        header: "Volume variation %",
        accessorKey: "volumeVariation",
        key: "volumeVariation",
        visible: false,
        dataType: "number",
        accessorFn: (row) => {
          return isNumberAvailable(row?.inboundRecord?.volumeVariation)
            ? parseAndRoundToThreeDecimal(
                row?.inboundRecord?.volumeVariation,
              ).toString()
            : "";
        },
      },
      {
        header: "Certified",
        accessorKey: "certified",
        key: "certified",
        visible: false,
        accessorFn: (row) => {
          return row?.inboundRecord?.certified || "";
        },
      },
      {
        header: "Received quantity MT",
        accessorKey: "receivedQuantityMt",
        key: "receivedQuantityMt",
        visible: false,
        dataType: "number",
        accessorFn: (row) => {
          return isNumberAvailable(row?.inboundRecord?.receivedQuantityMt)
            ? parseAndRoundToThreeDecimal(
                row?.inboundRecord?.receivedQuantityMt,
              ).toString()
            : "";
        },
      },
      {
        header: "Actualized quantity (in) MT",
        accessorKey: "actualizedQuantityMt",
        key: "actualizedQuantityMt",
        visible: false,
        dataType: "number",
        accessorFn: (row) => {
          return isNumberAvailable(row?.inboundRecord?.actualizedQuantityMt)
            ? parseAndRoundToThreeDecimal(
                row?.inboundRecord?.actualizedQuantityMt,
              ).toString()
            : "";
        },
      },
      {
        header: "GHG savings %",
        accessorKey: "ghgSavings",
        key: "ghgSavings",
        visible: false,
        dataType: "number",
        accessorFn: (row) => {
          return isNumberAvailable(row?.inboundRecord?.ghgSavings)
            ? parseAndRoundToThreeDecimal(
                row?.inboundRecord?.ghgSavings,
              ).toString()
            : "";
        },
      },
    ],
  },
  {
    header: "Outgoing",
    columns: [
      {
        header: "Quantity(out) m³",
        accessorKey: "quantityOutM3",
        key: "quantityOutM3",
        visible: true,
        disabled: true,
        filterFn: "massBalanceCustomFilterFunction",
        dataType: "number",
        accessorFn: (row) => {
          return isNumberAvailable(row?.outboundRecords?.quantityOutM3)
            ? parseAndRoundToThreeDecimal(
                row?.outboundRecords?.quantityOutM3,
              ).toString()
            : "";
        },
        cell: ({row}) => {
          const value = row?.original.outboundRecords?.quantityOutM3;
          return value && parseAndRoundToThreeDecimal(value).toString();
        },
      },
      {
        header: "Outbound type",
        accessorKey: "outboundType",
        key: "outboundType",
        visible: true,
        meta: {
          filterVariant: "select",
        },
        filterFn: "massBalanceCustomFilterFunction",
        accessorFn: (row) => {
          return row?.outboundRecords?.outboundType;
        },
        cell: ({row}) => {
          return row?.original.outboundRecords?.outboundType;
        },
      },
      {
        header: "Recipient",
        accessorKey: "recipient",
        key: "recipient",
        visible: true,
        meta: {
          filterVariant: "select",
        },
        filterFn: "massBalanceCustomFilterFunction",
        accessorFn: (row) => {
          return row?.outboundRecords?.recipient;
        },
        cell: ({row}) => {
          return row?.original.outboundRecords?.recipient;
        },
      },
      {
        header: "Document",
        accessorKey: "document",
        key: "document",
        visible: true,
        filterFn: "massBalanceCustomFilterFunction",
        accessorFn: (row) => {
          return row?.outboundRecords?.document || "";
        },
        cell: ({cell, row}) => {
          return (
            <Link
              to={`/document-manager/incoming/${selectedCountry}/detailedView/${encodeURIComponent(
                row?.original?.inboundRecord?.certificateInboundEuId,
              )}`}
              className="link-dark"
            >
              <u className="flex">
                {row?.original.outboundRecords?.document && <FilePdf24 />}
                <ColumnHighlight
                  value={row?.original.outboundRecords?.document}
                  columnFilter={getFilterValue(cell, "document")}
                  hasEllipsis
                  isCertificate
                />
              </u>
            </Link>
          );
        },
      },
      {
        header: "Quantity(out) MT",
        accessorKey: "quantityOutMt",
        key: "quantityOutMt",
        visible: false,
        filterFn: "massBalanceCustomFilterFunction",
        dataType: "number",
        accessorFn: (row) => {
          return isNumberAvailable(row?.outboundRecords?.quantityOutMt)
            ? parseAndRoundToThreeDecimal(
                row?.outboundRecords?.quantityOutMt,
              ).toString()
            : "";
        },
        cell: ({row}) => {
          const value = row?.original.outboundRecords?.quantityOutMt;
          return value && parseAndRoundToThreeDecimal(value).toString();
        },
      },
      {
        header: "Issuance date",
        accessorKey: "issuanceDateOutgoing",
        key: "issuanceDateOutgoing",
        visible: false,
        meta: {
          filterVariant: "daterange",
        },
        filterFn: "dateRangeFilterFnOutgoing",
        accessorFn: (row) => {
          return row?.outboundRecords?.issuanceDateOutgoing;
        },
        cell: ({row}) => {
          return (
            row?.original.outboundRecords?.issuanceDateOutgoing &&
            moment(row?.original.outboundRecords?.issuanceDateOutgoing).format(
              dateFormat,
            )
          );
        },
      },
      {
        header: "Product",
        accessorKey: "product",
        key: "product",
        visible: false,
        meta: {
          filterVariant: "select",
        },
        filterFn: "massBalanceCustomFilterFunction",
        accessorFn: (row) => {
          return row?.outboundRecords?.product;
        },
        cell: ({row}) => {
          return row?.original.outboundRecords?.product;
        },
      },
      {
        header: "Transport system",
        accessorKey: "transportSystemOutgoing",
        key: "transportSystemOutgoing",
        visible: false,
        meta: {
          filterVariant: "select",
        },
        filterFn: "massBalanceCustomFilterFunction",
        accessorFn: (row) => {
          return row?.outboundRecords?.transportSystemOutgoing;
        },
        cell: ({row}) => {
          return row?.original.outboundRecords?.transportSystemOutgoing;
        },
      },
      {
        header: "Vessel name",
        accessorKey: "vesselNameOutgoing",
        key: "vesselNameOutgoing",
        visible: false,
        meta: {
          filterVariant: "select",
        },
        filterFn: "massBalanceCustomFilterFunction",
        accessorFn: (row) => {
          return row?.outboundRecords?.vesselNameOutgoing;
        },
        cell: ({row}) => {
          return row?.original.outboundRecords?.vesselNameOutgoing;
        },
      },
      {
        header: "GHG total gCO2eq/MJ",
        accessorKey: "ghgTotalMjOutgoing",
        key: "ghgTotalMjOutgoing",
        visible: false,
        filterFn: "massBalanceCustomFilterFunction",
        dataType: "number",
        accessorFn: (row) => {
          return isNumberAvailable(row?.outboundRecords?.ghgTotalMjOutgoing)
            ? parseAndRoundToThreeDecimal(
                row?.outboundRecords?.ghgTotalMjOutgoing,
              ).toString()
            : "";
        },
        cell: ({row}) => {
          return parseAndRoundToThreeDecimal(
            row?.original.outboundRecords?.ghgTotalMjOutgoing,
          ).toString();
        },
      },
      {
        header: "GHG savings %",
        accessorKey: "ghgSavingsOutgoing",
        key: "ghgSavingsOutgoing",
        visible: false,
        filterFn: "massBalanceCustomFilterFunction",
        dataType: "number",
        accessorFn: (row) => {
          return isNumberAvailable(row?.outboundRecords?.ghgSavingsOutgoing)
            ? parseAndRoundToThreeDecimal(
                row?.outboundRecords?.ghgSavingsOutgoing,
              ).toString()
            : "";
        },
        cell: ({row}) => {
          return row?.original.outboundRecords?.ghgSavingsOutgoing;
        },
      },
      {
        header: "Status",
        accessorKey: "status",
        key: "status",
        visible: true,
        disabled: true,
        meta: {
          filterVariant: "select",
        },
        filterFn: "massBalanceCustomFilterFunction",
        accessorFn: (row) => {
          return row?.outboundRecords?.status;
        },
        cell: ({row}) => {
          let currentStatus = row?.original.outboundRecords?.status;
          const fromMbLocationGroupId =
            row?.original.inboundRecord?.fromMbLocationGroupId;
          if (
            currentStatus === status.AVAILABLE &&
            fromMbLocationGroupId !== null
          ) {
            currentStatus = status.AVAILABLE_FROM_TRANSFER;
          }
          const bgColor = getStatusColor(currentStatus);
          return (
            <span
              className={`whitespace-nowrap uppercase status-badge-mass-balance disabled:text-white ${bgColor}`}
            >
              {getStatusValue(currentStatus)}
            </span>
          );
        },
      },
      {
        header: "",
        accessorKey: "actions",
        key: "actions",
        size: 25,
        visible: true,
        enableSorting: false,
        cell: ({row, table}) => {
          return (
            <ActionDropdown
              row={row}
              table={table}
              navigateToAllocation={navigateToAllocation}
              currentFilterValues={currentFilterValues}
            />
          );
        },
      },
    ],
  },
];

export const getColumnsTRSWithUK = (
  dateFormat,
  decimalFormatNew,
  selectedCountry,
  siteReferenceId,
  divisionId,
  currentFilterValues,
) => [
  {
    header: "Incoming",
    columns: [
      {
        header: "Issuance date",
        accessorKey: "issuanceDate",
        key: "issuanceDate",
        visible: true,
        meta: {
          filterVariant: "daterange",
        },
        filterFn: "dateRangeFilterFn",
        accessorFn: (row) => {
          return row?.inboundRecord?.issuanceDate;
        },
        cell: ({cell}) => {
          return cell.getValue() && moment(cell.getValue()).format(dateFormat);
        },
      },
      {
        header: "Document",
        accessorKey: "incomingDocument",
        key: "incomingDocument",
        visible: true,
        size: 500,
        accessorFn: (row) => {
          return row?.inboundRecord?.incomingDocument;
        },
        cell: ({cell, row}) => {
          const tooltipId = `incomingDocument${row?.id}`;
          return (
            <>
              <Link
                to={{
                  pathname: `/document-manager/incoming/${selectedCountry}/detailedView/${encodeURIComponent(
                    row?.original?.inboundRecord?.certificateInboundEuId,
                  )}`,
                  search: `?pageNo=0&siteReferenceId=${siteReferenceId}&divisionId=${divisionId}`,
                }}
                className="link-dark"
                id={tooltipId}
              >
                <u className="flex">
                  {cell.getValue() && <FilePdf24 />}
                  <ColumnHighlight
                    value={cell.getValue()}
                    columnFilter={getFilterValue(cell, "incomingDocument")}
                    hasEllipsis
                    isCertificate
                  />
                </u>
              </Link>

              <UncontrolledTooltip placement="bottom" target={tooltipId}>
                {cell.getValue() ?? ""}
              </UncontrolledTooltip>
            </>
          );
        },
      },
      {
        header: "Raw material",
        accessorKey: "rawMaterial",
        key: "rawMaterial",
        visible: true,
        meta: {
          filterVariant: "select",
        },
        accessorFn: (row) => {
          return row?.inboundRecord?.rawMaterial;
        },
      },
      {
        header: "Country of origin",
        accessorKey: "countryOfOrigin",
        key: "countryOfOrigin",
        visible: true,
        meta: {
          filterVariant: "select",
        },
        accessorFn: (row) => {
          return row?.inboundRecord?.countryOfOrigin;
        },
      },
      {
        header: "GHG savings %",
        accessorKey: "ghgSavings",
        key: "ghgSavings",
        visible: true,
        dataType: "number",
        accessorFn: (row) => {
          return row?.inboundRecord?.ghgSavings?.toString();
        },
      },
      {
        header: "Quantity(in) m³",
        accessorKey: "productQtyM3",
        key: "productQtyM3",
        visible: true,
        disabled: true,
        dataType: "number",
        accessorFn: (row) => {
          // Need to update key here
          return row?.inboundRecord?.actualizedQuantityM3?.toString();
        },
      },
      {
        header: "Physical receipt date",
        accessorKey: "physicalReceiptDate",
        key: "physicalReceiptDate",
        visible: false,
        meta: {
          filterVariant: "daterange",
        },
        filterFn: "dateRangeFilterFn",
        accessorFn: (row) => {
          return row?.inboundRecord?.physicalReceiptDate;
        },
        cell: ({cell}) => {
          return cell.getValue() && moment(cell.getValue()).format(dateFormat);
        },
      },
      {
        header: "Quantity MT",
        accessorKey: "productQtyMt",
        key: "productQtyMt",
        visible: false,
        dataType: "number",
        accessorFn: (row) => {
          return row?.inboundRecord?.productQtyMt?.toString();
        },
      },
      {
        header: "Supplier",
        accessorKey: "supplier",
        key: "supplier",
        visible: false,
        meta: {
          filterVariant: "select",
        },
        accessorFn: (row) => {
          return row?.inboundRecord?.supplier;
        },
      },
      {
        header: "Transport system",
        accessorKey: "transportSystem",
        key: "transportSystem",
        visible: false,
        meta: {
          filterVariant: "select",
        },
        accessorFn: (row) => {
          return row?.inboundRecord?.transportSystem;
        },
      },
      {
        header: "Vessel name",
        accessorKey: "vesselName",
        key: "vesselName",
        visible: false,
        meta: {
          filterVariant: "select",
        },
        accessorFn: (row) => {
          return row?.inboundRecord?.vesselName;
        },
      },
      {
        header: "Invoice number",
        accessorKey: "invoiceNumber",
        key: "invoiceNumber",
        visible: false,
        accessorFn: (row) => {
          return row?.inboundRecord?.invoiceNumber;
        },
      },
      {
        header: "Transport loss/gain",
        accessorKey: "transportLg",
        key: "transportLg",
        visible: false,
        meta: {
          filterVariant: "select",
        },
        accessorFn: (row) => {
          return row?.inboundRecord?.transportLg;
        },
      },
      {
        header: "Volume variation %",
        accessorKey: "volumeVariation",
        key: "volumeVariation",
        visible: false,
        dataType: "number",
        accessorFn: (row) => {
          return row?.inboundRecord?.volumeVariation?.toString();
        },
      },
      {
        header: "Certified",
        accessorKey: "certified",
        key: "certified",
        visible: false,
        accessorFn: (row) => {
          return row?.inboundRecord?.certified;
        },
      },
      {
        header: "Received quantity MT",
        accessorKey: "receivedQuantityMt",
        key: "receivedQuantityMt",
        visible: false,
        dataType: "number",
        accessorFn: (row) => {
          return row?.inboundRecord?.receivedQuantityMt?.toString();
        },
      },
      {
        header: "Actualized quantity (in) m³",
        accessorKey: "actualizedQuantityM3",
        key: "actualizedQuantityM3",
        visible: false,
        dataType: "number",
        accessorFn: (row) => {
          return row?.inboundRecord?.actualizedQuantityM3?.toString();
        },
      },
      {
        header: "Actualized quantity (in) MT",
        accessorKey: "actualizedQuantityMt",
        key: "actualizedQuantityMt",
        visible: false,
        dataType: "number",
        accessorFn: (row) => {
          return row?.inboundRecord?.actualizedQuantityMt?.toString();
        },
      },
      {
        header: "GHG total gCO2eq/MJ",
        accessorKey: "ghgTotalMj",
        key: "ghgTotalMj",
        visible: false,
        dataType: "number",
        accessorFn: (row) => {
          return row?.inboundRecord?.ghgTotalMj?.toString();
        },
        cell: ({cell}) => {
          return parseAndRoundToThreeDecimal(cell.getValue());
        },
      },
    ],
  },
  {
    header: "Outgoing",
    columns: [
      {
        header: "Quantity(out) m³",
        accessorKey: "quantityOutM3",
        key: "quantityOutM3",
        visible: true,
        disabled: true,
        filterFn: "massBalanceCustomFilterFunction",
        dataType: "number",
        accessorFn: (row) => {
          return row?.outboundRecords?.quantityOutM3?.toString();
        },
        cell: ({row}) => {
          return parseAndRoundToThreeDecimal(
            row?.original.outboundRecords?.quantityOutM3,
          );
        },
      },
      {
        header: "Outbound type",
        accessorKey: "outboundType",
        key: "outboundType",
        visible: true,
        meta: {
          filterVariant: "select",
        },
        filterFn: "massBalanceCustomFilterFunction",
        accessorFn: (row) => {
          return row?.outboundRecords?.outboundType;
        },
        cell: ({row}) => {
          return row?.original.outboundRecords?.outboundType;
        },
      },
      {
        header: "Recipient",
        accessorKey: "recipient",
        key: "recipient",
        visible: true,
        meta: {
          filterVariant: "select",
        },
        filterFn: "massBalanceCustomFilterFunction",
        accessorFn: (row) => {
          return row?.outboundRecords?.recipient;
        },
        cell: ({row}) => {
          return row?.original.outboundRecords?.recipient;
        },
      },
      {
        header: "Quantity(out) MT",
        accessorKey: "quantityOutMt",
        key: "quantityOutMt",
        visible: false,
        filterFn: "massBalanceCustomFilterFunction",
        dataType: "number",
        accessorFn: (row) => {
          return row?.outboundRecords?.quantityOutMt?.toString();
        },
        cell: ({row}) => {
          return row?.original.outboundRecords?.quantityOutMt;
        },
      },
      {
        header: "Document",
        accessorKey: "document",
        key: "document",
        visible: false,
        filterFn: "massBalanceCustomFilterFunction",
        accessorFn: (row) => {
          return row?.outboundRecords?.document;
        },
        cell: ({cell, row}) => {
          return (
            <Link
              to={`/document-manager/incoming/${selectedCountry}/detailedView/${encodeURIComponent(
                row?.original?.inboundRecord?.certificateInboundEuId,
              )}`}
              className="link-dark"
            >
              <u className="flex">
                {row?.original.outboundRecords?.document && <FilePdf24 />}
                <ColumnHighlight
                  value={row?.original.outboundRecords?.document}
                  columnFilter={getFilterValue(cell, "document")}
                  hasEllipsis
                  isCertificate
                />
              </u>
            </Link>
          );
        },
      },
      {
        header: "Issuance date",
        accessorKey: "issuanceDateOutgoing",
        key: "issuanceDateOutgoing",
        visible: false,
        meta: {
          filterVariant: "daterange",
        },
        filterFn: "dateRangeFilterFnOutgoing",
        accessorFn: (row) => {
          return row?.outboundRecords?.issuanceDateOutgoing;
        },
        cell: ({row}) => {
          return (
            row?.original.outboundRecords?.issuanceDateOutgoing &&
            moment(row?.original.outboundRecords?.issuanceDateOutgoing).format(
              dateFormat,
            )
          );
        },
      },
      {
        header: "Product",
        accessorKey: "product",
        key: "product",
        visible: false,
        meta: {
          filterVariant: "select",
        },
        filterFn: "massBalanceCustomFilterFunction",
        accessorFn: (row) => {
          return row?.outboundRecords?.product;
        },
        cell: ({row}) => {
          return row?.original.outboundRecords?.product;
        },
      },
      {
        header: "Transport system",
        accessorKey: "transportSystemOutgoing",
        key: "transportSystemOutgoing",
        visible: false,
        meta: {
          filterVariant: "select",
        },
        filterFn: "massBalanceCustomFilterFunction",
        accessorFn: (row) => {
          return row?.outboundRecords?.transportSystemOutgoing;
        },
        cell: ({row}) => {
          return row?.original.outboundRecords?.transportSystemOutgoing;
        },
      },
      {
        header: "Vessel name",
        accessorKey: "vesselNameOutgoing",
        key: "vesselNameOutgoing",
        visible: false,
        meta: {
          filterVariant: "select",
        },
        filterFn: "massBalanceCustomFilterFunction",
        accessorFn: (row) => {
          return row?.outboundRecords?.vesselNameOutgoing;
        },
        cell: ({row}) => {
          return row?.original.outboundRecords?.vesselNameOutgoing;
        },
      },
      {
        header: "GHG total gCO2eq/MJ",
        accessorKey: "ghgTotalMjOutgoing",
        key: "ghgTotalMjOutgoing",
        visible: false,
        filterFn: "massBalanceCustomFilterFunction",
        dataType: "number",
        accessorFn: (row) => {
          return row?.outboundRecords?.ghgTotalMjOutgoing?.toString();
        },
        cell: ({row}) => {
          return parseAndRoundToThreeDecimal(
            row?.original.outboundRecords?.ghgTotalMjOutgoing,
          );
        },
      },
      {
        header: "GHG savings %",
        accessorKey: "ghgSavingsOutgoing",
        key: "ghgSavingsOutgoing",
        visible: false,
        filterFn: "massBalanceCustomFilterFunction",
        dataType: "number",
        accessorFn: (row) => {
          return row?.outboundRecords?.ghgSavingsOutgoing?.toString();
        },
        cell: ({row}) => {
          return row?.original.outboundRecords?.ghgSavingsOutgoing;
        },
      },
      {
        header: "Status",
        accessorKey: "status",
        key: "status",
        visible: true,
        disabled: true,
        meta: {
          filterVariant: "select",
        },
        filterFn: "massBalanceCustomFilterFunction",
        accessorFn: (row) => {
          return row?.outboundRecords?.status;
        },
        cell: ({row}) => {
          const status = row?.original.outboundRecords?.status;
          const bgColor = getStatusColor(status);
          return (
            <span
              className={`whitespace-nowrap uppercase status-badge-mass-balance disabled:text-white ${bgColor}`}
            >
              {getStatusValue(status)}
            </span>
          );
        },
      },
      {
        header: "",
        accessorKey: "actions",
        key: "actions",
        size: 25,
        visible: true,
        enableSorting: false,
        cell: ({row, table}) => {
          return (
            <ActionDropdown
              row={row}
              table={table}
              currentFilterValues={currentFilterValues}
            />
          );
        },
      },
    ],
  },
];

export const getColumnsCPP = (
  dateFormat,
  decimalFormatNew,
  selectedCountry,
  currentFilterValues,
) => [
  {
    header: "Incoming",
    columns: [
      {
        header: "Physical receipt date",
        accessorKey: "physicalReceiptDate",
        key: "physicalReceiptDate",
        visible: true,
        meta: {
          filterVariant: "daterange",
        },
        filterFn: "dateRangeFilterFn",
        accessorFn: (row) => {
          return row?.inboundRecord?.physicalReceiptDate;
        },
        cell: ({cell}) => {
          return cell.getValue() && moment(cell.getValue()).format(dateFormat);
        },
      },
      {
        header: "Document",
        accessorKey: "incomingDocument",
        key: "incomingDocument",
        visible: true,
        size: 500,
        accessorFn: (row) => {
          return row?.inboundRecord?.incomingDocument;
        },
        cell: ({cell, row}) => {
          const tooltipId = `incomingDocument${row?.id}`;
          return (
            <>
              <Link
                to={`/document-manager/incoming/${selectedCountry}/detailedView/${encodeURIComponent(
                  row?.original?.inboundRecord?.certificateInboundEuId,
                )}`}
                className="link-dark"
                id={tooltipId}
              >
                <u className="flex">
                  {cell.getValue() && <FilePdf24 />}
                  <ColumnHighlight
                    value={cell.getValue()}
                    columnFilter={getFilterValue(cell, "incomingDocument")}
                    hasEllipsis
                    isCertificate
                  />
                </u>
              </Link>
              <UncontrolledTooltip placement="bottom" target={tooltipId}>
                {cell.getValue() ?? ""}
              </UncontrolledTooltip>
            </>
          );
        },
      },
      {
        header: "Quantity MT",
        accessorKey: "productQtyMt",
        key: "productQtyMt",
        visible: false,
        dataType: "number",
        accessorFn: (row) => {
          return row?.inboundRecord?.productQtyMt?.toString();
        },
      },
      {
        header: "Country of origin",
        accessorKey: "countryOfOrigin",
        key: "countryOfOrigin",
        visible: false,
        meta: {
          filterVariant: "select",
        },
        accessorFn: (row) => {
          return row?.inboundRecord?.countryOfOrigin;
        },
      },
      {
        header: "Supplier",
        accessorKey: "supplier",
        key: "supplier",
        visible: false,
        meta: {
          filterVariant: "select",
        },
        accessorFn: (row) => {
          return row?.inboundRecord?.supplier;
        },
      },
      {
        header: "Transport system",
        accessorKey: "transportSystem",
        key: "transportSystem",
        visible: false,
        meta: {
          filterVariant: "select",
        },
        accessorFn: (row) => {
          return row?.inboundRecord?.transportSystem;
        },
      },
      {
        header: "Vessel name",
        accessorKey: "vesselName",
        key: "vesselName",
        visible: false,
        meta: {
          filterVariant: "select",
        },
        accessorFn: (row) => {
          return row?.inboundRecord?.vesselName;
        },
      },
      {
        header: "Invoice number",
        accessorKey: "invoiceNumber",
        key: "invoiceNumber",
        visible: false,
        accessorFn: (row) => {
          return row?.inboundRecord?.invoiceNumber;
        },
      },
      {
        header: "Transport loss/gain",
        accessorKey: "transportLg",
        key: "transportLg",
        visible: false,
        meta: {
          filterVariant: "select",
        },
        accessorFn: (row) => {
          return row?.inboundRecord?.transportLg;
        },
      },
      {
        header: "Certified",
        accessorKey: "certified",
        key: "certified",
        visible: false,
        accessorFn: (row) => {
          return row?.inboundRecord?.certified;
        },
      },
      {
        header: "Received quantity MT",
        accessorKey: "receivedQuantityMt",
        key: "receivedQuantityMt",
        visible: true,
        dataType: "number",
        accessorFn: (row) => {
          return row?.inboundRecord?.receivedQuantityMt?.toString();
        },
      },
      {
        header: "GHG total gCO2eq/MJ",
        accessorKey: "ghgTotalMj",
        key: "ghgTotalMj",
        visible: true,
        dataType: "number",
        accessorFn: (row) => {
          return row?.inboundRecord?.ghgTotalMj?.toString();
        },
        cell: ({cell}) => {
          return parseAndRoundToThreeDecimal(cell.getValue());
        },
      },
    ],
  },
  {
    header: "Co-processed",
    columns: [
      {
        header: "Co-processed quantity (out) MT",
        accessorKey: "coProcessedQuantityMt",
        key: "coProcessedQuantityMt",
        visible: true,
        dataType: "number",
        accessorFn: (row) => {
          return row?.inboundRecord?.coProcessedQuantityMt?.toString();
        },
      },
    ],
  },
  {
    header: "Outgoing",
    columns: [
      {
        header: "Quantity(out) MT",
        accessorKey: "quantityOutMt",
        key: "quantityOutMt",
        visible: true,
        filterFn: "massBalanceCustomFilterFunction",
        dataType: "number",
        accessorFn: (row) => {
          return row?.outboundRecords?.quantityOutMt?.toString();
        },
        cell: ({row}) => {
          return row?.original.outboundRecords?.quantityOutMt;
        },
      },
      {
        header: "Document",
        accessorKey: "document",
        key: "document",
        visible: true,
        filterFn: "massBalanceCustomFilterFunction",
        accessorFn: (row) => {
          return row?.outboundRecords?.document;
        },
        cell: ({cell, row}) => {
          return (
            <Link
              to={`/document-manager/incoming/${selectedCountry}/detailedView/${encodeURIComponent(
                row?.original?.inboundRecord?.certificateInboundEuId,
              )}`}
              className="link-dark"
            >
              <u className="flex">
                {row?.original.outboundRecords?.document && <FilePdf24 />}
                <ColumnHighlight
                  value={row?.original.outboundRecords?.document}
                  columnFilter={getFilterValue(cell, "document")}
                  hasEllipsis
                  isCertificate
                />
              </u>
            </Link>
          );
        },
      },
      {
        header: "Issuance date",
        accessorKey: "issuanceDateOutgoing",
        key: "issuanceDateOutgoing",
        visible: false,
        meta: {
          filterVariant: "daterange",
        },
        filterFn: "dateRangeFilterFnOutgoing",
        accessorFn: (row) => {
          return row?.outboundRecords?.issuanceDateOutgoing;
        },
        cell: ({row}) => {
          return (
            row?.original.outboundRecords?.issuanceDateOutgoing &&
            moment(row?.original.outboundRecords?.issuanceDateOutgoing).format(
              dateFormat,
            )
          );
        },
      },
      {
        header: "Product",
        accessorKey: "product",
        key: "product",
        visible: false,
        meta: {
          filterVariant: "select",
        },
        filterFn: "massBalanceCustomFilterFunction",
        accessorFn: (row) => {
          return row?.outboundRecords?.product;
        },
        cell: ({row}) => {
          return row?.original.outboundRecords?.product;
        },
      },
      {
        header: "Recipient",
        accessorKey: "recipient",
        key: "recipient",
        visible: false,
        meta: {
          filterVariant: "select",
        },
        filterFn: "massBalanceCustomFilterFunction",
        accessorFn: (row) => {
          return row?.outboundRecords?.recipient;
        },
        cell: ({row}) => {
          return row?.original.outboundRecords?.recipient;
        },
      },
      {
        header: "Transport system",
        accessorKey: "transportSystemOutgoing",
        key: "transportSystemOutgoing",
        visible: false,
        meta: {
          filterVariant: "select",
        },
        filterFn: "massBalanceCustomFilterFunction",
        accessorFn: (row) => {
          return row?.outboundRecords?.transportSystemOutgoing;
        },
        cell: ({row}) => {
          return row?.original.outboundRecords?.transportSystemOutgoing;
        },
      },
      {
        header: "Vessel name",
        accessorKey: "vesselNameOutgoing",
        key: "vesselNameOutgoing",
        visible: false,
        meta: {
          filterVariant: "select",
        },
        filterFn: "massBalanceCustomFilterFunction",
        accessorFn: (row) => {
          return row?.outboundRecords?.vesselNameOutgoing;
        },
        cell: ({row}) => {
          return row?.original.outboundRecords?.vesselNameOutgoing;
        },
      },
      {
        header: "GHG total gCO2eq/MJ",
        accessorKey: "ghgTotalMjOutgoing",
        key: "ghgTotalMjOutgoing",
        visible: false,
        filterFn: "massBalanceCustomFilterFunction",
        dataType: "number",
        accessorFn: (row) => {
          return row?.outboundRecords?.ghgTotalMjOutgoing?.toString();
        },
        cell: ({row}) => {
          return parseAndRoundToThreeDecimal(
            row?.original.outboundRecords?.ghgTotalMjOutgoing,
          );
        },
      },
      {
        header: "GHG savings %",
        accessorKey: "ghgSavingsOutgoing",
        key: "ghgSavingsOutgoing",
        visible: false,
        filterFn: "massBalanceCustomFilterFunction",
        dataType: "number",
        accessorFn: (row) => {
          return row?.outboundRecords?.ghgSavingsOutgoing?.toString();
        },
        cell: ({row}) => {
          return row?.original.outboundRecords?.ghgSavingsOutgoing;
        },
      },
      {
        header: "Status",
        accessorKey: "status",
        key: "status",
        visible: true,
        disabled: true,
        meta: {
          filterVariant: "select",
        },
        filterFn: "massBalanceCustomFilterFunction",
        accessorFn: (row) => {
          return row?.outboundRecords?.status;
        },
        cell: ({row}) => {
          const status = row?.original.outboundRecords?.status;
          const bgColor = getStatusColor(status);
          return (
            <span
              className={`whitespace-nowrap uppercase status-badge-mass-balance disabled:text-white ${bgColor}`}
            >
              {getStatusValue(status)}
            </span>
          );
        },
      },
      {
        header: "",
        accessorKey: "actions",
        key: "actions",
        size: 25,
        visible: true,
        enableSorting: false,
        cell: ({row, table}) => {
          return (
            <ActionDropdown
              row={row}
              table={table}
              currentFilterValues={currentFilterValues}
            />
          );
        },
      },
    ],
  },
];
